import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import TicketCard from '../components/TicketCard';
import { SOCCER_CLUBS } from '../constants/soccerData';
import firebaseService from '../services/firebaseService';
import { useLanguage } from '../context/LanguageContext';
import './DaysForecast.css';

const localizedDateNames = {
  ka: {
    months: ['იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი', 'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი'],
    weekdays: ['კვირა', 'ორშაბათი', 'სამშაბათი', 'ოთხშაბათი', 'ხუთშაბათი', 'პარასკევი', 'შაბათი'],
    shortWeekdays: ['ორშ', 'სამ', 'ოთხ', 'ხუთ', 'პარ', 'შაბ', 'კვი']
  },
  en: {
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    shortWeekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  },
  ru: {
    months: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'],
    weekdays: ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'],
    shortWeekdays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
  }
};

const DaysForecast = () => {
  const { language, t } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingCards, setUpdatingCards] = useState(new Set());

  // History section state
  const [historyTickets, setHistoryTickets] = useState([]);
  const [filteredHistoryTickets, setFilteredHistoryTickets] = useState([]);
  const [historySelectedDate, setHistorySelectedDate] = useState(null);
  const [historySelectedClub, setHistorySelectedClub] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showClubDropdown, setShowClubDropdown] = useState(false);
  const [clubSearchTerm, setClubSearchTerm] = useState('');
  const [dateMode, setDateMode] = useState('single'); // 'single' | 'range' | 'month'
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [calendarTarget, setCalendarTarget] = useState('single');
  const [selectedMonthFilter, setSelectedMonthFilter] = useState(null);
  const [monthNavDate, setMonthNavDate] = useState(new Date());

  const activeDateNames = localizedDateNames[language] || localizedDateNames.ka;
  const georgianMonths = activeDateNames.months;
  const georgianWeekdays = activeDateNames.shortWeekdays;

  // All clubs for dropdown
  const allClubs = Object.values(SOCCER_CLUBS).flat();
  const clubs = ['ყველა კლუბი', ...allClubs];

  // Filter clubs based on search term
  const filteredClubs = clubs.filter(club =>
    club.toLowerCase().includes(clubSearchTerm.toLowerCase())
  );



  useEffect(() => {
    let unsubscribe = null;
    let isMounted = true;

    const setupSubscription = async () => {
      try {
        // Set up real-time listener for automatic updates
        unsubscribe = await firebaseService.subscribeToMatches((updatedMatches) => {
          if (!isMounted) return;

          console.log('🔔 DaysForecast - Real-time update:', updatedMatches?.length || 0, 'matches');

          // Get today's date and nearby dates in YYYY-MM-DD format
          const today = new Date();
          const todayStr = today.toISOString().split('T')[0];
          const tomorrowStr = new Date(Date.now() + 86400000).toISOString().split('T')[0];
          const yesterdayStr = new Date(Date.now() - 86400000).toISOString().split('T')[0];
          const twoDaysAgoStr = new Date(Date.now() - 172800000).toISOString().split('T')[0];
          const threeDaysAgoStr = new Date(Date.now() - 259200000).toISOString().split('T')[0];

          const validDates = [todayStr, tomorrowStr, yesterdayStr, twoDaysAgoStr, threeDaysAgoStr];

          // Filter: Show matches (regular category) with pending or live status from today or nearby dates
          let todayMatches = updatedMatches.filter(match => {
            const matchDate = match.date ? match.date.split('T')[0] : null;
            return (
              (match.status === 'pending' || match.status === 'live') &&
              match.category === 'regular' &&
              validDates.includes(matchDate)
            );
          });

          // If no matches found in recent dates, fallback to showing all pending/live regular matches
          if (todayMatches.length === 0) {
            todayMatches = updatedMatches.filter(match => {
              return (
                (match.status === 'pending' || match.status === 'live') &&
                match.category === 'regular'
              );
            });
          }

          console.log('📊 DaysForecast - Updated state (regular category only):', todayMatches.length, 'matches');

          setTickets(todayMatches);

          // Track which cards are being updated
          const updatingIds = new Set();
          todayMatches.forEach(newMatch => {
            const oldMatch = tickets.find(m => m.id === newMatch.id);
            if (oldMatch && JSON.stringify(oldMatch) !== JSON.stringify(newMatch)) {
              updatingIds.add(newMatch.id);
            }
          });

          if (updatingIds.size > 0) {
            setUpdatingCards(updatingIds);
            // Clear the updating state after animation
            setTimeout(() => {
              if (isMounted) setUpdatingCards(new Set());
            }, 1000);
          }
        });
      } catch (error) {
        console.error('❌ Error setting up subscription:', error);
      }
    };

    const fetchTickets = async () => {
      if (!isMounted) return;

      setLoading(true);

      try {
        // Load real matches from Firebase
        const matches = await firebaseService.getAllMatches();
        console.log('📥 DaysForecast - Loaded:', matches?.length || 0, 'matches');
        if (!isMounted) return;

        // Get today's date and nearby dates in YYYY-MM-DD format
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0];
        const tomorrowStr = new Date(Date.now() + 86400000).toISOString().split('T')[0];
        const yesterdayStr = new Date(Date.now() - 86400000).toISOString().split('T')[0];
        const twoDaysAgoStr = new Date(Date.now() - 172800000).toISOString().split('T')[0];
        const threeDaysAgoStr = new Date(Date.now() - 259200000).toISOString().split('T')[0];

        const validDates = [todayStr, tomorrowStr, yesterdayStr, twoDaysAgoStr, threeDaysAgoStr];

        // Filter: Show matches (regular category) with pending or live status from today or nearby dates
        let todayMatches = matches.filter(match => {
          const matchDate = match.date ? match.date.split('T')[0] : null;
          return (
            (match.status === 'pending' || match.status === 'live') &&
            match.category === 'regular' &&
            validDates.includes(matchDate)
          );
        });

        // If no matches found in recent dates, fallback to showing all pending/live regular matches
        if (todayMatches.length === 0) {
          todayMatches = matches.filter(match => {
            return (
              (match.status === 'pending' || match.status === 'live') &&
              match.category === 'regular'
            );
          });
        }

        // Filter: Historical matches (finished, win, loss) for all categories
        let historicalMatches = matches.filter(match =>
          (match.status === 'win' || match.status === 'loss' || match.status === 'finished') &&
          (match.category === 'regular' || match.category === 'high-risk' || match.category === 'fantasy' || match.category === 'live')
        );

        console.log('✅ DaysForecast - Setting state (regular category only):', todayMatches.length, 'tickets');
        console.log('✅ DaysForecast - Setting history state:', historicalMatches.length, 'historical tickets');
        setTickets(todayMatches);
        setHistoryTickets(historicalMatches);
      } catch (error) {
        console.error('❌ Error loading forecast matches:', error);
        if (!isMounted) return;
        setTickets([]);
        setHistoryTickets([]);
      }

      if (isMounted) setLoading(false);
    };

    fetchTickets();
    setupSubscription();

    // Cleanup function
    return () => {
      isMounted = false;
      if (unsubscribe && typeof unsubscribe === 'function') {
        try {
          unsubscribe();
        } catch (error) {
          console.error('Error unsubscribing:', error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  useEffect(() => {
    // Filter tickets by selected team
    setFilteredTickets(tickets);
  }, [tickets]);

  // Filter history tickets based on search term, selected date, and club
  useEffect(() => {
    let filtered = historyTickets;

    if (dateMode === 'single' && historySelectedDate) {
      const selectedDateStr = format(historySelectedDate, 'yyyy-MM-dd');
      filtered = filtered.filter(ticket => {
        if (!ticket.date) return false;
        return ticket.date.split('T')[0] === selectedDateStr;
      });
    } else if (dateMode === 'range' && dateFrom) {
      const fromStr = format(dateFrom, 'yyyy-MM-dd');
      const toStr = dateTo ? format(dateTo, 'yyyy-MM-dd') : fromStr;
      filtered = filtered.filter(ticket => {
        if (!ticket.date) return false;
        const d = ticket.date.split('T')[0];
        return d >= fromStr && d <= toStr;
      });
    } else if (dateMode === 'month' && selectedMonthFilter) {
      const monthStr = format(selectedMonthFilter, 'yyyy-MM');
      filtered = filtered.filter(ticket => {
        if (!ticket.date) return false;
        return ticket.date.split('T')[0].substring(0, 7) === monthStr;
      });
    }

    if (historySelectedClub && historySelectedClub !== 'ყველა კლუბი') {
      filtered = filtered.filter(ticket =>
        ticket.homeTeam === historySelectedClub || ticket.awayTeam === historySelectedClub
      );
    }

    setFilteredHistoryTickets(filtered);
  }, [historyTickets, historySelectedDate, dateFrom, dateTo, selectedMonthFilter, dateMode, historySelectedClub]);

  // Calendar functions
  const formatGeorgianDate = (date) => {
    const day = date.getDate();
    const month = georgianMonths[date.getMonth()];
    const year = date.getFullYear();
    return language === 'ka' ? `${day} ${month} ${year}` : `${month} ${day}, ${year}`;
  };

  const getDaysInMonth = (date) => {
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    return eachDayOfInterval({ start, end });
  };

  const hasTicketsOnDate = (date) => {
    return historyTickets.some(ticket => {
      if (!ticket.date) return false;
      const ticketDate = ticket.date.split('T')[0];
      const checkDate = format(date, 'yyyy-MM-dd');
      return ticketDate === checkDate;
    });
  };

  const handleDateSelect = (date) => {
    if (calendarTarget === 'from') {
      setDateFrom(date);
      if (dateTo && date > dateTo) setDateTo(null);
      setCalendarTarget('to');
      // keep calendar open for 'to'
    } else if (calendarTarget === 'to') {
      if (dateFrom && date < dateFrom) {
        setDateFrom(date);
        setDateTo(dateFrom);
      } else {
        setDateTo(date);
      }
      setShowCalendar(false);
    } else {
      setHistorySelectedDate(date);
      setShowCalendar(false);
    }
  };

  const clearDateFilter = () => {
    setHistorySelectedDate(null);
    setDateFrom(null);
    setDateTo(null);
    setSelectedMonthFilter(null);
  };

  const isInRange = (date) => {
    if (dateMode !== 'range' || !dateFrom || !dateTo) return false;
    const d = format(date, 'yyyy-MM-dd');
    return d > format(dateFrom, 'yyyy-MM-dd') && d < format(dateTo, 'yyyy-MM-dd');
  };

  const hasActiveFilter =
    (dateMode === 'single' && historySelectedDate) ||
    (dateMode === 'range' && (dateFrom || dateTo)) ||
    (dateMode === 'month' && selectedMonthFilter);

  const handleDateModeChange = (mode) => {
    setDateMode(mode);
    setHistorySelectedDate(null);
    setDateFrom(null);
    setDateTo(null);
    setSelectedMonthFilter(null);
    setShowCalendar(false);
    setCalendarTarget('single');
  };

  const handleCalendarBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowCalendar(false);
    }
  };

  // Handle escape key and click outside
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        if (showCalendar) setShowCalendar(false);
        if (showClubDropdown) setShowClubDropdown(false);
      }
    };

    const handleClickOutside = (e) => {
      if (showClubDropdown && !e.target.closest('.club-filter-wrapper')) {
        setShowClubDropdown(false);
      }
    };

    if (showCalendar) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    if (showClubDropdown) {
      document.addEventListener('keydown', handleEscapeKey);
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [showCalendar, showClubDropdown]);

  const getTodayDate = () => {
    const today = new Date();
    const weekday = activeDateNames.weekdays[today.getDay()];
    const month = activeDateNames.months[today.getMonth()];
    const day = today.getDate();
    const year = today.getFullYear();

    if (language === 'ka') return `${day} ${month}, ${weekday}, ${year}`;
    return language === 'en'
      ? `${weekday}, ${month} ${day}, ${year}`
      : `${weekday}, ${day} ${month} ${year}`;
  };

  if (loading) {
    return (
      <div className="days-forecast">
        <div className="container">
          <div className="loading-container">
            <div className="spinner"></div>
            <p className="loading-text">{t('forecast.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="days-forecast"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <motion.h1
            className="page-title"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            {t('nav.forecast')}
          </motion.h1>
          <motion.p
            className="page-subtitle"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {getTodayDate()}
          </motion.p>
        </div>







        {/* Tickets */}
        <div className="tickets-section">
          {filteredTickets.length > 0 ? (
            <div className="tickets-grid">
              <div className="tc-header">
                <span className="tc-header-label">{t('forecast.clubs')}</span>
                <span className="tc-header-label">{t('forecast.prediction')}</span>
                <span className="tc-header-label">{t('forecast.coefficientDate')}</span>
              </div>
              {filteredTickets.map((ticket, index) => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  index={index}
                  isUpdating={updatingCards.has(ticket.id)}
                />
              ))}
            </div>
          ) : (
            <div className="empty-container">
              <div className="empty-icon">📊</div>
              <h3 className="empty-title">{t('forecast.noForecasts')}</h3>
              <p className="empty-message">
                {t('forecast.noForecastsMessage')}
              </p>
            </div>
          )}
        </div>

        {/* History Section */}
        <motion.div
          className="history-section"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          <h3 className="history-title">📊 {t('forecast.history')}</h3>

          {/* Modern Search Filters */}
          <motion.div
            className="filters-container-modern"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            {/* Combined Date and Club Filters */}
            <div className="filter-card date-filter-card combined-filter-card">
              {/* Date mode selector */}
              <div className="date-mode-selector">
                <button
                  className={`date-mode-btn ${dateMode === 'single' ? 'active' : ''}`}
                  onClick={() => handleDateModeChange('single')}
                >
                  📅 {t('filters.single')}
                </button>
                <button
                  className={`date-mode-btn ${dateMode === 'range' ? 'active' : ''}`}
                  onClick={() => handleDateModeChange('range')}
                >
                  📆 {t('filters.range')}
                </button>
                <button
                  className={`date-mode-btn ${dateMode === 'month' ? 'active' : ''}`}
                  onClick={() => handleDateModeChange('month')}
                >
                  🗓️ {t('filters.month')}
                </button>
              </div>

              <div className="filter-controls-row">
                {/* Single date mode */}
                {dateMode === 'single' && (
                  <div className="date-filter">
                    <button
                      className="date-selector-modern"
                      onClick={() => { setCalendarTarget('single'); setShowCalendar(!showCalendar); }}
                    >
                      <span>{historySelectedDate ? formatGeorgianDate(historySelectedDate) : t('filters.chooseDate')}</span>
                      <span className="dropdown-arrow">▼</span>
                    </button>
                    {historySelectedDate && (
                      <button className="clear-button" onClick={clearDateFilter} title={t('common.clear')}>✕</button>
                    )}
                  </div>
                )}

              {/* Range mode */}
              {dateMode === 'range' && (
                <div className="date-range-container">
                  <div className="date-range-row">
                    <div className="date-range-field">
                      <span className="date-range-label">{t('filters.start')}</span>
                      <button
                        className={`date-range-btn ${calendarTarget === 'from' && showCalendar ? 'active-target' : ''}`}
                        onClick={() => { setCalendarTarget('from'); setShowCalendar(true); }}
                      >
                        {dateFrom ? formatGeorgianDate(dateFrom) : '-- / -- / ----'}
                      </button>
                    </div>
                    <span className="date-range-arrow">→</span>
                    <div className="date-range-field">
                      <span className="date-range-label">{t('filters.end')}</span>
                      <button
                        className={`date-range-btn ${calendarTarget === 'to' && showCalendar ? 'active-target' : ''}`}
                        onClick={() => { setCalendarTarget('to'); setShowCalendar(true); }}
                      >
                        {dateTo ? formatGeorgianDate(dateTo) : '-- / -- / ----'}
                      </button>
                    </div>
                    {(dateFrom || dateTo) && (
                      <button className="clear-button" onClick={clearDateFilter} title={t('common.clear')}>✕</button>
                    )}
                  </div>
                  {showCalendar && dateMode === 'range' && (
                    <p className="range-hint">
                      {calendarTarget === 'from' ? '👆 აირჩიეთ საწყისი თარიღი' : '👆 აირჩიეთ საბოლოო თარიღი'}
                    </p>
                  )}
                </div>
              )}

              {/* Month mode */}
              {dateMode === 'month' && (
                <div className="month-filter-container">
                  <div className="month-nav">
                    <button
                      className="nav-button-modern"
                      onClick={() => setMonthNavDate(new Date(monthNavDate.getFullYear(), monthNavDate.getMonth() - 1))}
                    >‹</button>
                    <span className="month-nav-label">
                      {georgianMonths[monthNavDate.getMonth()]} {monthNavDate.getFullYear()}
                    </span>
                    <button
                      className="nav-button-modern"
                      onClick={() => setMonthNavDate(new Date(monthNavDate.getFullYear(), monthNavDate.getMonth() + 1))}
                    >›</button>
                  </div>
                  <div className="month-action-row">
                    <button
                      className={`select-month-btn ${selectedMonthFilter && format(selectedMonthFilter, 'yyyy-MM') === format(monthNavDate, 'yyyy-MM') ? 'selected' : ''}`}
                      onClick={() => setSelectedMonthFilter(new Date(monthNavDate.getFullYear(), monthNavDate.getMonth(), 1))}
                    >
                      {selectedMonthFilter && format(selectedMonthFilter, 'yyyy-MM') === format(monthNavDate, 'yyyy-MM')
                        ? '✓ შერჩეულია'
                        : 'ამ თვის არჩევა'}
                    </button>
                    {selectedMonthFilter && (
                      <button className="clear-button" onClick={clearDateFilter} title={t('common.clear')}>✕</button>
                    )}
                  </div>
                </div>
              )}

              {/* Shared calendar modal (single + range modes) */}
              {showCalendar && dateMode !== 'month' && (
                <div className="calendar-modal" onClick={handleCalendarBackdropClick}>
                  <motion.div
                    className="calendar-content-modern"
                    onClick={(e) => e.stopPropagation()}
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="calendar-sheet-label">
                      <span aria-hidden="true">📅</span>
                      <span>{dateMode === 'range' ? (calendarTarget === 'from' ? t('filters.start') : t('filters.end')) : t('filters.chooseDate')}</span>
                    </div>
                    <div className="calendar-header-modern">
                      <button
                        className="nav-button-modern"
                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                        aria-label="წინა თვე"
                      >‹</button>
                      <span className="month-year-modern">
                        {georgianMonths[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                      </span>
                      <button
                        className="nav-button-modern"
                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                        aria-label="შემდეგი თვე"
                      >›</button>
                      <button
                        className="close-calendar-modern"
                        onClick={() => setShowCalendar(false)}
                        aria-label="დახურვა"
                      >✕</button>
                    </div>
                    <div className="weekdays-row">
                      {georgianWeekdays.map((day, idx) => (
                        <div key={idx} className="weekday-label">{day}</div>
                      ))}
                    </div>
                    <div className="calendar-grid-modern">
                      {(() => {
                        const firstDay = getDaysInMonth(currentMonth)[0];
                        const dayOfWeek = firstDay.getDay();
                        const offset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                        return Array.from({ length: offset }, (_, i) => (
                          <div key={`spacer-${i}`} className="calendar-day-spacer" />
                        ));
                      })()}
                      {getDaysInMonth(currentMonth).map(date => {
                        const dateStr = format(date, 'yyyy-MM-dd');
                        const isSelected =
                          (dateMode === 'single' && historySelectedDate && dateStr === format(historySelectedDate, 'yyyy-MM-dd')) ||
                          (dateMode === 'range' && ((dateFrom && dateStr === format(dateFrom, 'yyyy-MM-dd')) || (dateTo && dateStr === format(dateTo, 'yyyy-MM-dd'))));
                        const inRange = isInRange(date);
                        return (
                          <button
                            key={date.toISOString()}
                            className={`calendar-day-modern ${isSelected ? 'selected' : ''} ${inRange ? 'in-range' : ''} ${hasTicketsOnDate(date) ? 'has-tickets' : ''}`}
                            onClick={() => handleDateSelect(date)}
                            disabled={!hasTicketsOnDate(date)}
                            aria-label={`${format(date, 'd')} ${georgianMonths[date.getMonth()]}`}
                          >
                            {format(date, 'd')}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                </div>
              )}

                {/* Club Dropdown Filter with integrated search */}
                <div className="club-filter-wrapper">
                <div className="club-dropdown-container">
                  <button
                    className="club-dropdown-button"
                    onClick={() => setShowClubDropdown(!showClubDropdown)}
                  >
                    <span>{historySelectedClub && historySelectedClub !== 'ყველა კლუბი' ? historySelectedClub : t('filters.allClubs')}</span>
                    <span className="dropdown-arrow">▼</span>
                  </button>
                  {historySelectedClub && historySelectedClub !== 'ყველა კლუბი' && (
                    <button
                      className="clear-button"
                      onClick={() => setHistorySelectedClub('ყველა კლუბი')}
                      title={t('common.clear')}
                    >
                      ✕
                    </button>
                  )}

                  {showClubDropdown && (
                    <motion.div
                      className="club-dropdown-menu"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="club-search-wrapper">
                        <input
                          type="text"
                          className="club-search-input"
                          placeholder={`🔎 ${t('filters.searchClub')}`}
                          value={clubSearchTerm}
                          onChange={(e) => setClubSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      </div>
                      <div className="club-options-list">
                        {filteredClubs.length > 0 ? (
                          filteredClubs.map(club => (
                            <button
                              key={club}
                              className={`club-option ${historySelectedClub === club ? 'selected' : ''}`}
                              onClick={() => {
                                setHistorySelectedClub(club);
                                setShowClubDropdown(false);
                                setClubSearchTerm('');
                              }}
                            >
                            {club === 'ყველა კლუბი' ? `⚽ ${t('filters.allClubs')}` : `👉 ${club}`}
                            </button>
                          ))
                        ) : (
                          <div className="no-results">{t('filters.clubNotFound')}</div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
              </div>
            </div>
          </motion.div>

          {/* History Tickets */}
          <div className="history-tickets-section">
            {filteredHistoryTickets.length > 0 ? (
              (() => {
                // Group tickets by date
                const groupedByDate = filteredHistoryTickets.reduce((groups, ticket) => {
                  const dateKey = ticket.date ? ticket.date.split('T')[0] : 'unknown';
                  if (!groups[dateKey]) {
                    groups[dateKey] = [];
                  }
                  groups[dateKey].push(ticket);
                  return groups;
                }, {});

                // Sort dates in descending order (newest first)
                const sortedDates = Object.keys(groupedByDate).sort((a, b) => new Date(b) - new Date(a));

                return sortedDates.map(dateKey => {
                  const ticketsForDate = groupedByDate[dateKey];
                  const dateObj = new Date(dateKey);
                  const weekday = activeDateNames.weekdays[dateObj.getDay()];
                  const month = activeDateNames.months[dateObj.getMonth()];
                  const day = dateObj.getDate();
                  const year = dateObj.getFullYear();
                  const formattedDate = language === 'ka'
                    ? `${day} ${month}, ${weekday}, ${year}`
                    : language === 'en'
                      ? `${weekday}, ${month} ${day}, ${year}`
                      : `${weekday}, ${day} ${month} ${year}`;

                  return (
                    <div key={dateKey} className="date-group-section">
                      <h4 className="date-group-title">{formattedDate}</h4>
                      <div className="tickets-line">
                        <div className="tc-header">
                          <span className="tc-header-label">{t('forecast.clubs')}</span>
                          <span className="tc-header-label">{t('forecast.prediction')}</span>
                          <span className="tc-header-label">{t('forecast.coefficientDate')}</span>
                        </div>
                        {ticketsForDate.map((ticket, index) => (
                          <TicketCard
                            key={ticket.id}
                            ticket={ticket}
                            index={index}
                            showResult={true}
                          />
                        ))}
                      </div>
                    </div>
                  );
                });
              })()
            ) : (
              <div className="empty-container">
                <div className="empty-icon">📅</div>
                <h3 className="empty-title">{t('forecast.noHistory')}</h3>
                <p className="empty-message">
                  {historySelectedClub || hasActiveFilter ? t('forecast.noHistoryFiltered') : t('forecast.noHistoryAvailable')}
                </p>
              </div>
            )}
          </div>
        </motion.div>

      </div>
    </motion.div>
  );
};

export default DaysForecast;
